import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  IconButton,
  Divider,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { fetchApi } from "./fetchApi";


type ArtikelOption = {
  id: number;
  sku: string;
  bezeichnung: string;
};

type Item = {
  artikel_id: number | null; // Auswahl über SKU -> intern speichern wir id
  anzahl: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  artikelOptionen: ArtikelOption[];
  initialArtikelIds?: number[];
};

export default function EtikettenDruckDialog({
  open,
  onClose,
  artikelOptionen,
  initialArtikelIds = [],
}: Props) {
  const [startPosition, setStartPosition] = useState<number>(1);
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const initialItems = useMemo<Item[]>(() => {
    return initialArtikelIds.map((id) => ({ artikel_id: id, anzahl: 1 }));
  }, [initialArtikelIds]);

  const [items, setItems] = useState<Item[]>(initialItems);

  React.useEffect(() => {
    if (open) {
      setStartPosition(1);
      setItems(initialItems.length ? initialItems : []);
      setFehler(null);
      setLaden(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const artikelMap = useMemo(() => {
    const m = new Map<number, ArtikelOption>();
    for (let i = 0; i < artikelOptionen.length; i++) {
      const a = artikelOptionen[i];
      m.set(a.id, a);
    }
    return m;
  }, [artikelOptionen]);

  const addItem = () => {
    setItems((prev) => [...prev, { artikel_id: null, anzahl: 1 }]);
  };

  const updateItem = (idx: number, patch: Partial<Item>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = (): string | null => {
    if (!Number.isInteger(startPosition) || startPosition < 1 || startPosition > 40) {
      return "Startposition muss zwischen 1 und 40 liegen.";
    }
    if (!items.length) return "Bitte mindestens einen Artikel hinzufügen.";

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.artikel_id == null) return `Position ${i + 1}: Bitte eine SKU auswählen.`;
      if (!Number.isInteger(it.anzahl) || it.anzahl < 1) return `Position ${i + 1}: Anzahl muss >= 1 sein.`;
    }
    return null;
  };

  const handleDrucken = async () => {
    const v = validate();
    if (v) {
      setFehler(v);
      return;
    }

    setFehler(null);
    setLaden(true);

    try {
      // Backend erwartet artikel_id + anzahl
      const payload = {
        start_position: startPosition,
        items: items.map((it) => ({
          artikel_id: it.artikel_id as number,
          anzahl: it.anzahl,
        })),
      };

      const resp = await fetchApi("/api/etiketten/pdf", {
  method: "POST",
  body: JSON.stringify(payload),
});


      if (!resp.ok) {
        const data = await resp.json().catch(() => null);
        const msg =
          data?.errors?.join(" | ") ||
          data?.error ||
          `Fehler beim Erzeugen des PDFs (HTTP ${resp.status}).`;
        throw new Error(msg);
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);

      onClose();
    } catch (e: any) {
      setFehler(
  e?.message === "401"
    ? "Session abgelaufen. Bitte neu einloggen."
    : (e?.message || "Unbekannter Fehler.")
);

    } finally {
      setLaden(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Barcode-Etiketten drucken</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            A4 / 40 Etiketten (4×10). Startposition zählt spaltenweise:
            1–10 = Spalte 1, 11–20 = Spalte 2, 21–30 = Spalte 3, 31–40 = Spalte 4.
          </Typography>

          <TextField
            label="Startposition (1–40)"
            type="number"
            value={startPosition}
            onChange={(e) => setStartPosition(parseInt(e.target.value || "1", 10))}
            inputProps={{ min: 1, max: 40 }}
            sx={{ width: 260 }}
          />

          <Divider />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Positionen</Typography>
            <Button onClick={addItem} variant="outlined">
              Artikel hinzufügen
            </Button>
          </Stack>

          {items.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Noch keine Positionen. Bitte „Artikel hinzufügen“.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {items.map((it, idx) => {
                const selected = it.artikel_id != null ? artikelMap.get(it.artikel_id) || null : null;

                return (
                  <Stack
                    key={idx}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ p: 1, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 2 }}
                  >
                    <Autocomplete
                      options={artikelOptionen}
                      value={selected}
                      onChange={(_, newValue) => {
                        updateItem(idx, { artikel_id: newValue ? newValue.id : null });
                      }}
                      getOptionLabel={(opt) => `${opt.sku} – ${opt.bezeichnung}`}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      renderInput={(params) => (
                        <TextField {...params} label="SKU" placeholder="SKU suchen..." />
                      )}
                      sx={{ flex: 1, minWidth: 320 }}
                    />

                    <TextField
                      label="Anzahl"
                      type="number"
                      value={it.anzahl}
                      onChange={(e) => updateItem(idx, { anzahl: parseInt(e.target.value || "1", 10) })}
                      inputProps={{ min: 1 }}
                      sx={{ width: 140 }}
                    />

                    <IconButton onClick={() => removeItem(idx)} aria-label="Löschen">
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          )}

          {fehler && (
            <Typography variant="body2" color="error">
              {fehler}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={laden}>
          Abbrechen
        </Button>
        <Button onClick={handleDrucken} variant="contained" disabled={laden || items.length === 0}>
          {laden ? "Erzeuge PDF..." : "PDF öffnen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
